/**
 * Wanan Search — Custom search & filtering powered by Meilisearch
 *
 * Replaces Shopify native Search & Discovery.
 * Handles: autocomplete, collection filtering, faceted navigation, infinite scroll.
 */
(function () {
  'use strict';

  const CONFIG = {
    apiUrl: window.__wananSearch?.apiUrl || '',
    meiliUrl: window.__wananSearch?.meiliUrl || '',    // Direct Meilisearch mode (local dev)
    meiliKey: window.__wananSearch?.meiliKey || '',
    store: window.__wananSearch?.store || 'us',
    currency: window.__wananSearch?.currency || 'USD',
    currencySymbol: window.__wananSearch?.currencySymbol || '$',
    locale: window.__wananSearch?.locale || 'en',
    collectionHandle: window.__wananSearch?.collectionHandle || '',
    collectionTitle: window.__wananSearch?.collectionTitle || '',
    collectionVendor: window.__wananSearch?.collectionVendor || '',
    collectionGender: window.__wananSearch?.collectionGender || '',
    collectionMacro: window.__wananSearch?.collectionMacro || '',
    showPriceComparison: window.__wananSearch?.showPriceComparison || 0,
    currentTags: window.__wananSearch?.currentTags || [],
    searchQuery: window.__wananSearch?.searchQuery || '',
    pageSize: 60,
    debounceMs: 250,
    autocompleteMinChars: 2,
  };

  // =========================================================================
  // Utility
  // =========================================================================

  function debounce(fn, ms) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str ?? '';
    return div.innerHTML;
  }

  // Capitalize first letter of each word (for category/color/gender display)
  // Do NOT use on vendor names (they have their own casing: "A.P.C.", "D&G", etc.)
  const TITLECASE_FACETS = new Set(['macro_category', 'sub_category', 'detail_category', 'gender', 'color']);
  function titleCase(str) {
    if (!str) return '';
    return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  }
  // Localized display labels for normalized facet values. The underlying
  // value (data-value / filter query) stays English — this is display-only.
  const FACET_VALUE_I18N = {
    it: {
      gender: { men: 'Uomo', women: 'Donna' },
      // Display-only EN->IT for the raw Meilisearch color values.
      // Vocabulary follows the Wanan color anagrafica; granular shades
      // kept distinct (no count aggregation here — that needs the index).
      color: {
        'black': 'Nero', 'white': 'Bianco', 'blue': 'Blu',
        'navy': 'Blu Navy', 'dark blue': 'Blu Scuro', 'light blue': 'Azzurro',
        'red': 'Rosso', 'coral': 'Corallo', 'green': 'Verde',
        'olive': 'Verde Oliva', 'khaki': 'Kaki', 'teal': 'Ottanio',
        'yellow': 'Giallo', 'orange': 'Arancione', 'pink': 'Rosa',
        'fuchsia': 'Fucsia', 'purple': 'Viola', 'brown': 'Marrone',
        'cognac': 'Cognac', 'tan': 'Cuoio', 'camel': 'Cammello',
        'beige': 'Beige', 'taupe': 'Tortora', 'ecru': 'Ecru',
        'cream': 'Crema', 'ivory': 'Avorio', 'nude': 'Nude',
        'grey': 'Grigio', 'gray': 'Grigio', 'light grey': 'Grigio Chiaro',
        'dark grey': 'Grigio Scuro', 'charcoal': 'Antracite',
        'silver': 'Argento', 'gold': 'Oro', 'bordeaux': 'Bordeaux',
        'burgundy': 'Amaranto', 'multicolor': 'Multicolor',
      },
    },
    en: {
      macro_category: {
        "accessori": "Accessories", "borse": "Bags", "beauty": "Beauty",
        "abbigliamento": "Clothing", "casa e lifestyle": "Home & Lifestyle", "gioielli": "Jewellery",
        "scarpe": "Shoes",
      },
      sub_category: {
        "zaini e valigie": "Backpacks & Travel", "abbigliamento da mare": "Beachwear", "cinture": "Belts",
        "stivali e stivaletti": "Boots & Booties", "bracciali": "Bracelets", "spille": "Brooches & Pins",
        "pochette": "Clutch Bags", "capispalla": "Coats", "orecchini": "Earrings",
        "espadrillas": "Espadrilles", "scarpe basse": "Flat Shoes", "gilet": "Gilets",
        "borse a mano": "Hand Bags", "cappelli": "Hats", "casa e lifestyle": "Home & Lifestyle",
        "giacche e giubbini": "Jackets", "tute intere": "Jumpsuits", "portachiavi": "Keychains",
        "mocassini": "Loafers", "mules": "Mules", "collane": "Necklaces",
        "stringate": "Oxford Shoes", "pantaloni e jeans": "Pants & Denim", "cellulare, pc e gadget": "Phone, Laptop & Gadgets",
        "scarpe alte": "Pumps", "anelli": "Rings", "sandali": "Sandals",
        "sciarpe e guanti": "Scarves & Gloves", "camicie e bluse": "Shirts & Blouses", "shorts": "Shorts",
        "borse a spalla e tracolla": "Shoulder Bags", "gonne": "Skirts", "sneakers": "Sneakers",
        "abiti": "Suits & Dresses", "occhiali da sole": "Sunglasses", "t shirt & polo": "T-shirts & Polos",
        "cravatte e papillon": "Ties & Bow Ties", "top": "Tops", "felpe e maglioni": "Knitwear & Sweatshirts",
        "ombrelli": "Umbrellas", "intimo": "Underwear", "portafogli e portacarte": "Wallets & Card Holders",
        "orologi": "Watches", "accessori per capelli": "Hair Accessories", "beauty case": "Beauty Cases",
        "profumi": "Perfumes", "libri": "Books", "accessori per la casa": "Home Accessories",
        "accessori per animali": "Pet Accessories", "occhiali da vista": "Eyeglasses",
      },
      detail_category: {
        "ballerine": "Ballet Flats", "teli mare": "Beach Towels", "beachwear": "Beachwear",
        "marsupi": "Belt Bags", "blazer": "Blazers", "blouse": "Blouses",
        "bomber": "Bombers", "stivaletti": "Booties", "stivali": "Boots",
        "borse a secchiello": "Bucket Bags", "caban": "Peacoats", "mantelle": "Capes",
        "cardigan": "Cardigans", "cappotti": "Coats", "copricostume": "Cover-Ups",
        "jeans": "Denim", "pellicce e parka": "Furs & Parkas", "felpe con cappuccio": "Hoodies",
        "giacche": "Jackets", "capi in pelle": "Leather Jackets", "leggins": "Leggings",
        "giacconi": "Overcoats", "pantaloni": "Pants", "polo": "Polo Shirts",
        "piumini": "Puffer Jackets", "decollete": "Pumps", "camicie": "Shirts",
        "slip on": "Slip-Ons", "ciabatte": "Slippers", "calze": "Socks",
        "zeppe": "Wedges", "maglioni": "Sweaters", "felpe": "Sweatshirts",
        "costumi": "Swimwear", "t shirt": "T-shirts", "tote bags": "Tote Bags",
        "trench e impermeabili": "Trench Coats", "sciarpe": "Scarves", "guanti": "Gloves",
      },
    },
    fr: {
      gender: {
        "men": "Homme", "women": "Femme",
      },
      color: {
        "black": "Noir", "white": "Blanc", "blue": "Bleu",
        "navy": "Bleu Marine", "dark blue": "Bleu Foncé", "light blue": "Bleu Clair",
        "red": "Rouge", "coral": "Corail", "green": "Vert",
        "olive": "Vert Olive", "khaki": "Kaki", "teal": "Bleu Canard",
        "yellow": "Jaune", "orange": "Orange", "pink": "Rose",
        "fuchsia": "Fuchsia", "purple": "Violet", "brown": "Marron",
        "cognac": "Cognac", "tan": "Cuir", "camel": "Camel",
        "beige": "Beige", "taupe": "Taupe", "ecru": "Écru",
        "cream": "Crème", "ivory": "Ivoire", "nude": "Nude",
        "grey": "Gris", "gray": "Gris", "light grey": "Gris Clair",
        "dark grey": "Gris Foncé", "charcoal": "Anthracite", "silver": "Argent",
        "gold": "Or", "bordeaux": "Bordeaux", "burgundy": "Bourgogne",
        "multicolor": "Multicolore", "dark green": "Vert Foncé",
      },
      macro_category: {
        "accessori": "Accessoires", "borse": "Sacs", "beauty": "Beauté",
        "abbigliamento": "Vêtements", "casa e lifestyle": "Maison & Lifestyle", "gioielli": "Bijoux",
        "scarpe": "Chaussures",
      },
      sub_category: {
        "zaini e valigie": "Sacs à Dos & Bagages", "abbigliamento da mare": "Beachwear", "cinture": "Ceintures",
        "stivali e stivaletti": "Bottes & Bottines", "bracciali": "Bracelets", "spille": "Broches",
        "pochette": "Pochettes", "capispalla": "Manteaux", "orecchini": "Boucles d'Oreilles",
        "espadrillas": "Espadrilles", "scarpe basse": "Chaussures Plates", "gilet": "Gilets",
        "borse a mano": "Sacs à Main", "cappelli": "Chapeaux", "casa e lifestyle": "Maison & Lifestyle",
        "giacche e giubbini": "Vestes & Blousons", "tute intere": "Combinaisons", "portachiavi": "Porte-clés",
        "mocassini": "Mocassins", "mules": "Mules", "collane": "Colliers",
        "stringate": "Derbies & Richelieus", "pantaloni e jeans": "Pantalons & Jeans", "cellulare, pc e gadget": "Téléphone, PC & Gadgets",
        "scarpe alte": "Escarpins", "anelli": "Bagues", "sandali": "Sandales",
        "sciarpe e guanti": "Écharpes & Gants", "camicie e bluse": "Chemises & Blouses", "shorts": "Shorts",
        "borse a spalla e tracolla": "Sacs à Épaule & Bandoulière", "gonne": "Jupes", "sneakers": "Sneakers",
        "abiti": "Robes & Tailleurs", "occhiali da sole": "Lunettes de Soleil", "t shirt & polo": "T-shirts & Polos",
        "cravatte e papillon": "Cravates & Nœuds Papillon", "top": "Tops", "felpe e maglioni": "Pulls & Sweats",
        "ombrelli": "Parapluies", "intimo": "Sous-vêtements", "portafogli e portacarte": "Portefeuilles & Porte-cartes",
        "orologi": "Montres", "accessori per capelli": "Accessoires Cheveux", "beauty case": "Trousses de Toilette",
        "profumi": "Parfums", "libri": "Livres", "accessori per la casa": "Accessoires Maison",
        "accessori per animali": "Accessoires pour Animaux", "occhiali da vista": "Lunettes de Vue",
      },
      detail_category: {
        "ballerine": "Ballerines", "teli mare": "Serviettes de Plage", "beachwear": "Beachwear",
        "marsupi": "Sacs Banane", "blazer": "Blazers", "blouse": "Blouses",
        "bomber": "Bombers", "stivaletti": "Bottines", "stivali": "Bottes",
        "borse a secchiello": "Sacs Seau", "caban": "Cabans", "mantelle": "Capes",
        "cardigan": "Cardigans", "cappotti": "Manteaux", "copricostume": "Tuniques de Plage",
        "jeans": "Jeans", "pellicce e parka": "Fourrures & Parkas", "felpe con cappuccio": "Sweats à Capuche",
        "giacche": "Vestes", "capi in pelle": "Vestes en Cuir", "leggins": "Leggings",
        "giacconi": "Manteaux Courts", "pantaloni": "Pantalons", "polo": "Polos",
        "piumini": "Doudounes", "decollete": "Escarpins", "camicie": "Chemises",
        "slip on": "Slip-On", "ciabatte": "Claquettes", "calze": "Chaussettes",
        "zeppe": "Compensées", "maglioni": "Pulls", "felpe": "Sweats",
        "costumi": "Maillots de Bain", "t shirt": "T-shirts", "tote bags": "Tote Bags",
        "trench e impermeabili": "Trenchs & Imperméables", "sciarpe": "Écharpes", "guanti": "Gants",
      },
    },
    de: {
      gender: {
        "men": "Herren", "women": "Damen",
      },
      color: {
        "black": "Schwarz", "white": "Weiß", "blue": "Blau",
        "navy": "Marineblau", "dark blue": "Dunkelblau", "light blue": "Hellblau",
        "red": "Rot", "coral": "Koralle", "green": "Grün",
        "olive": "Olivgrün", "khaki": "Khaki", "teal": "Petrol",
        "yellow": "Gelb", "orange": "Orange", "pink": "Rosa",
        "fuchsia": "Fuchsia", "purple": "Violett", "brown": "Braun",
        "cognac": "Cognac", "tan": "Hellbraun", "camel": "Camel",
        "beige": "Beige", "taupe": "Taupe", "ecru": "Ecru",
        "cream": "Creme", "ivory": "Elfenbein", "nude": "Nude",
        "grey": "Grau", "gray": "Grau", "light grey": "Hellgrau",
        "dark grey": "Dunkelgrau", "charcoal": "Anthrazit", "silver": "Silber",
        "gold": "Gold", "bordeaux": "Bordeaux", "burgundy": "Burgunderrot",
        "multicolor": "Mehrfarbig", "dark green": "Dunkelgrün",
      },
      macro_category: {
        "accessori": "Accessoires", "borse": "Taschen", "beauty": "Beauty",
        "abbigliamento": "Bekleidung", "casa e lifestyle": "Haus & Lifestyle", "gioielli": "Schmuck",
        "scarpe": "Schuhe",
      },
      sub_category: {
        "zaini e valigie": "Rucksäcke & Reisegepäck", "abbigliamento da mare": "Beachwear", "cinture": "Gürtel",
        "stivali e stivaletti": "Stiefel & Stiefeletten", "bracciali": "Armbänder", "spille": "Broschen",
        "pochette": "Clutches", "capispalla": "Mäntel", "orecchini": "Ohrringe",
        "espadrillas": "Espadrilles", "scarpe basse": "Flache Schuhe", "gilet": "Westen",
        "borse a mano": "Handtaschen", "cappelli": "Hüte", "casa e lifestyle": "Haus & Lifestyle",
        "giacche e giubbini": "Jacken", "tute intere": "Jumpsuits", "portachiavi": "Schlüsselanhänger",
        "mocassini": "Loafer", "mules": "Mules", "collane": "Halsketten",
        "stringate": "Schnürschuhe", "pantaloni e jeans": "Hosen & Jeans", "cellulare, pc e gadget": "Handy, PC & Gadgets",
        "scarpe alte": "Pumps", "anelli": "Ringe", "sandali": "Sandalen",
        "sciarpe e guanti": "Schals & Handschuhe", "camicie e bluse": "Hemden & Blusen", "shorts": "Shorts",
        "borse a spalla e tracolla": "Schulter- & Umhängetaschen", "gonne": "Röcke", "sneakers": "Sneaker",
        "abiti": "Kleider & Anzüge", "occhiali da sole": "Sonnenbrillen", "t shirt & polo": "T-Shirts & Poloshirts",
        "cravatte e papillon": "Krawatten & Fliegen", "top": "Tops", "felpe e maglioni": "Pullover & Sweatshirts",
        "ombrelli": "Regenschirme", "intimo": "Unterwäsche", "portafogli e portacarte": "Geldbörsen & Kartenetuis",
        "orologi": "Uhren", "accessori per capelli": "Haarschmuck", "beauty case": "Kulturtaschen",
        "profumi": "Parfums", "libri": "Bücher", "accessori per la casa": "Wohnaccessoires",
        "accessori per animali": "Haustierzubehör", "occhiali da vista": "Brillen",
      },
      detail_category: {
        "ballerine": "Ballerinas", "teli mare": "Strandtücher", "beachwear": "Beachwear",
        "marsupi": "Gürteltaschen", "blazer": "Blazer", "blouse": "Blusen",
        "bomber": "Bomberjacken", "stivaletti": "Stiefeletten", "stivali": "Stiefel",
        "borse a secchiello": "Beuteltaschen", "caban": "Cabanjacken", "mantelle": "Capes",
        "cardigan": "Cardigans", "cappotti": "Mäntel", "copricostume": "Strandkleider",
        "jeans": "Jeans", "pellicce e parka": "Pelze & Parkas", "felpe con cappuccio": "Hoodies",
        "giacche": "Jacken", "capi in pelle": "Lederjacken", "leggins": "Leggings",
        "giacconi": "Kurzmäntel", "pantaloni": "Hosen", "polo": "Poloshirts",
        "piumini": "Daunenjacken", "decollete": "Pumps", "camicie": "Hemden",
        "slip on": "Slip-Ons", "ciabatte": "Pantoletten", "calze": "Socken",
        "zeppe": "Keilabsätze", "maglioni": "Pullover", "felpe": "Sweatshirts",
        "costumi": "Bademode", "t shirt": "T-Shirts", "tote bags": "Shopper",
        "trench e impermeabili": "Trenchcoats & Regenmäntel", "sciarpe": "Schals", "guanti": "Handschuhe",
      },
    },
    es: {
      gender: {
        "men": "Hombre", "women": "Mujer",
      },
      color: {
        "black": "Negro", "white": "Blanco", "blue": "Azul",
        "navy": "Azul Marino", "dark blue": "Azul Oscuro", "light blue": "Celeste",
        "red": "Rojo", "coral": "Coral", "green": "Verde",
        "olive": "Verde Oliva", "khaki": "Caqui", "teal": "Verde Azulado",
        "yellow": "Amarillo", "orange": "Naranja", "pink": "Rosa",
        "fuchsia": "Fucsia", "purple": "Morado", "brown": "Marrón",
        "cognac": "Coñac", "tan": "Cuero", "camel": "Camel",
        "beige": "Beige", "taupe": "Topo", "ecru": "Crudo",
        "cream": "Crema", "ivory": "Marfil", "nude": "Nude",
        "grey": "Gris", "gray": "Gris", "light grey": "Gris Claro",
        "dark grey": "Gris Oscuro", "charcoal": "Antracita", "silver": "Plata",
        "gold": "Oro", "bordeaux": "Burdeos", "burgundy": "Granate",
        "multicolor": "Multicolor", "dark green": "Verde Oscuro",
      },
      macro_category: {
        "accessori": "Accesorios", "borse": "Bolsos", "beauty": "Belleza",
        "abbigliamento": "Ropa", "casa e lifestyle": "Casa y Lifestyle", "gioielli": "Joyas",
        "scarpe": "Zapatos",
      },
      sub_category: {
        "zaini e valigie": "Mochilas y Maletas", "abbigliamento da mare": "Ropa de Playa", "cinture": "Cinturones",
        "stivali e stivaletti": "Botas y Botines", "bracciali": "Pulseras", "spille": "Broches",
        "pochette": "Clutches", "capispalla": "Abrigos", "orecchini": "Pendientes",
        "espadrillas": "Alpargatas", "scarpe basse": "Zapatos Planos", "gilet": "Chalecos",
        "borse a mano": "Bolsos de Mano", "cappelli": "Sombreros", "casa e lifestyle": "Casa y Lifestyle",
        "giacche e giubbini": "Chaquetas y Cazadoras", "tute intere": "Monos", "portachiavi": "Llaveros",
        "mocassini": "Mocasines", "mules": "Mules", "collane": "Collares",
        "stringate": "Zapatos de Cordones", "pantaloni e jeans": "Pantalones y Vaqueros", "cellulare, pc e gadget": "Móvil, PC y Gadgets",
        "scarpe alte": "Zapatos de Salón", "anelli": "Anillos", "sandali": "Sandalias",
        "sciarpe e guanti": "Bufandas y Guantes", "camicie e bluse": "Camisas y Blusas", "shorts": "Shorts",
        "borse a spalla e tracolla": "Bolsos de Hombro y Bandolera", "gonne": "Faldas", "sneakers": "Zapatillas",
        "abiti": "Vestidos y Trajes", "occhiali da sole": "Gafas de Sol", "t shirt & polo": "Camisetas y Polos",
        "cravatte e papillon": "Corbatas y Pajaritas", "top": "Tops", "felpe e maglioni": "Jerséis y Sudaderas",
        "ombrelli": "Paraguas", "intimo": "Ropa Interior", "portafogli e portacarte": "Carteras y Tarjeteros",
        "orologi": "Relojes", "accessori per capelli": "Accesorios para el Pelo", "beauty case": "Neceseres",
        "profumi": "Perfumes", "libri": "Libros", "accessori per la casa": "Accesorios para el Hogar",
        "accessori per animali": "Accesorios para Mascotas", "occhiali da vista": "Gafas Graduadas",
      },
      detail_category: {
        "ballerine": "Bailarinas", "teli mare": "Toallas de Playa", "beachwear": "Ropa de Playa",
        "marsupi": "Riñoneras", "blazer": "Blazers", "blouse": "Blusas",
        "bomber": "Bombers", "stivaletti": "Botines", "stivali": "Botas",
        "borse a secchiello": "Bolsos Saco", "caban": "Chaquetones Marineros", "mantelle": "Capas",
        "cardigan": "Cárdigans", "cappotti": "Abrigos", "copricostume": "Salidas de Baño",
        "jeans": "Vaqueros", "pellicce e parka": "Pieles y Parkas", "felpe con cappuccio": "Sudaderas con Capucha",
        "giacche": "Chaquetas", "capi in pelle": "Prendas de Piel", "leggins": "Leggings",
        "giacconi": "Chaquetones", "pantaloni": "Pantalones", "polo": "Polos",
        "piumini": "Plumíferos", "decollete": "Zapatos de Salón", "camicie": "Camisas",
        "slip on": "Slip-On", "ciabatte": "Chanclas", "calze": "Calcetines",
        "zeppe": "Cuñas", "maglioni": "Jerséis", "felpe": "Sudaderas",
        "costumi": "Bañadores", "t shirt": "Camisetas", "tote bags": "Bolsos Tote",
        "trench e impermeabili": "Gabardinas e Impermeables", "sciarpe": "Bufandas", "guanti": "Guantes",
      },
    },
  };
  function formatFacetValue(key, value) {
    if (!value) return '';
    const i18n = FACET_VALUE_I18N[CONFIG.locale]?.[key];
    const translated = i18n?.[String(value).toLowerCase()];
    if (translated) return translated;
    if (TITLECASE_FACETS.has(key)) return titleCase(value);
    return value; // vendor, sizes etc. keep original casing
  }

  function formatPrice(amount, maxDecimals = 0, minDecimals = 0) {
    const num = parseFloat(amount);
    if (!isFinite(num)) return '';
    if (CONFIG.currency === 'EUR') {
      return num.toLocaleString('it-IT', { minimumFractionDigits: minDecimals, maximumFractionDigits: maxDecimals }) + '€';
    }
    return CONFIG.currencySymbol + num.toLocaleString('en-US', { minimumFractionDigits: minDecimals, maximumFractionDigits: maxDecimals });
  }

  // Journey unica §3.4.2-bis: per un BUYER il proxy aggiunge `buyer.factor` (listino della sua location, es. 0.75).
  // L'indice porta prezzi RETAIL: la card mostra retail barrato + finale = retail × fattore. Senza fattore (retail,
  // VIP, o proxy che non lo manda) la card resta quella di sempre. Il finale ha i centesimi (585,75), il retail no.
  function buyerFactorFrom(result) {
    const f = result && result.buyer && result.buyer.factor;
    if (typeof f === 'number' && f > 0 && f < 1) state.buyerFactor = f;
  }
  // Barrato = il LISTINO retail (compare_at se c'e' ed e' maggiore, altrimenti il prezzo), come le schede Telegram.
  function buyerPriceHtml(hit, cls) {
    const f = state.buyerFactor;
    const retail = parseFloat(hit.price);
    if (!(f > 0 && f < 1) || !(retail > 0)) return null;
    const cmp = parseFloat(hit.compare_at_price);
    const listino = cmp > retail ? cmp : retail;
    const finale = Math.round(retail * f * 100) / 100;
    return `<span class="ws-price-compare wnn-retail">${formatPrice(listino)}</span> <span class="ws-price-sale ${cls || ''}">${formatPrice(finale, 2, Number.isInteger(finale) ? 0 : 2)}</span>`;
  }

  // =========================================================================
  // API Client
  // =========================================================================

  // Sanitize filter value — strip quotes to prevent filter injection
  function sanitizeFilterVal(v) {
    return String(v).replace(/["\\]/g, '');
  }

  // Map URL tags to gender filter values
  const TAG_GENDER_MAP = { 'men': 'Men', 'uomo': 'Men', 'women': 'Women', 'donna': 'Women' };

  function getGenderFromTags() {
    for (const tag of (CONFIG.currentTags || [])) {
      const mapped = TAG_GENDER_MAP[tag.toLowerCase()];
      if (mapped) return mapped;
    }
    return null;
  }

  // Direct Meilisearch filter builder (used in direct mode)
  function buildMeiliFilter(params) {
    const conds = ['available = true'];
    // Scope to current collection
    if (CONFIG.collectionHandle) {
      conds.push(`collections = "${sanitizeFilterVal(CONFIG.collectionHandle)}"`);
    }
    // Auto-filter gender from URL tag (e.g., /collections/new-in/Men)
    const tagGender = getGenderFromTags();
    if (tagGender && !params.gender) {
      conds.push(`gender = "${sanitizeFilterVal(tagGender)}"`);
    }
    for (const key of ['vendor', 'macro_category', 'sub_category', 'detail_category', 'gender', 'color']) {
      const v = params[key];
      if (!v) continue;
      if (Array.isArray(v)) {
        conds.push(`${key} IN [${v.map(x => '"' + sanitizeFilterVal(x) + '"').join(', ')}]`);
      } else {
        conds.push(`${key} = "${sanitizeFilterVal(v)}"`);
      }
    }
    const sizes = params.sizes;
    if (sizes) {
      if (Array.isArray(sizes)) {
        conds.push('(' + sizes.map(s => `sizes = "${sanitizeFilterVal(s)}"`).join(' OR ') + ')');
      } else {
        conds.push(`sizes = "${sanitizeFilterVal(sizes)}"`);
      }
    }
    // Extract scalar from array (price_min/price_max stored as ['500'])
    const pmin = parseFloat(Array.isArray(params.price_min) ? params.price_min[0] : params.price_min);
    const pmax = parseFloat(Array.isArray(params.price_max) ? params.price_max[0] : params.price_max);
    if (isFinite(pmin)) conds.push(`price >= ${pmin}`);
    if (isFinite(pmax)) conds.push(`price <= ${pmax}`);
    return conds.join(' AND ');
  }

  function buildMeiliSort(sort) {
    const map = { featured: null, newest: ['created_at:desc'], price_asc: ['price:asc'], price_desc: ['price:desc'], vendor_asc: ['vendor:asc'] };
    return map[sort] !== undefined ? map[sort] : null;
  }

  const FACETS = ['vendor', 'macro_category', 'sub_category', 'detail_category', 'gender', 'color', 'sizes', 'available'];
  const DISPLAY_FIELDS = [
    'id', 'shopify_id', 'title', 'vendor', 'handle', 'url',
    'image_url', 'image_url_hover', 'image_urls', 'price', 'compare_at_price', 'currency',
    'macro_category', 'sub_category', 'gender', 'color', 'sizes', 'available',
  ];

  // Direct Meilisearch mode — DEV ONLY. In production, only apiUrl should be set.
  const isDirect = !!CONFIG.meiliUrl;
  if (isDirect && location.hostname !== 'localhost' && !location.hostname.startsWith('127.')) {
    console.warn('WananSearch: Direct Meilisearch mode active on non-localhost — this exposes the API key. Use apiUrl instead.');
  }

  const api = {
    async search(params, signal) {
      try {
        if (isDirect) {
          const body = {
            q: params.q || '',
            filter: buildMeiliFilter(params),
            facets: FACETS,
            limit: parseInt(params.limit) || 24,
            offset: parseInt(params.offset) || 0,
            attributesToRetrieve: DISPLAY_FIELDS,
          };
          const sort = buildMeiliSort(params.sort);
          if (sort) body.sort = sort;
          const resp = await fetch(`${CONFIG.meiliUrl}/indexes/products_${CONFIG.store}/search`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${CONFIG.meiliKey}` },
            body: JSON.stringify(body),
            signal: signal || AbortSignal.timeout(5000),
          });
          if (!resp.ok) throw new Error(`Meilisearch ${resp.status}`);
          return await resp.json();
        }
        // API Gateway mode
        const qs = new URLSearchParams();
        for (const [k, v] of Object.entries(params)) {
          if (v !== null && v !== undefined && v !== '') {
            qs.set(k, Array.isArray(v) ? v.join(',') : v);
          }
        }
        const resp = await fetch(
          `${CONFIG.apiUrl}/search/${CONFIG.store}?${qs.toString()}`,
          { signal: signal || AbortSignal.timeout(5000) }
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        return await resp.json();
      } catch (e) {
        if (e.name === 'AbortError') return null; // cancelled, not an error
        console.warn('WananSearch: API error, falling back', e);
        return null;
      }
    },

    async autocomplete(query, signal) {
      if (query.length < CONFIG.autocompleteMinChars) return { hits: [] };
      try {
        const sig = signal || AbortSignal.timeout(3000);
        if (isDirect) {
          const resp = await fetch(`${CONFIG.meiliUrl}/indexes/products_${CONFIG.store}/search`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${CONFIG.meiliKey}` },
            body: JSON.stringify({
              q: query, limit: 6,
              attributesToRetrieve: ['title', 'vendor', 'handle', 'url', 'image_url', 'price', 'currency'],
              attributesToHighlight: ['title'],
              highlightPreTag: '<mark>', highlightPostTag: '</mark>',
            }),
            signal: sig,
          });
          if (!resp.ok) throw new Error(`Meilisearch ${resp.status}`);
          return await resp.json();
        }
        const resp = await fetch(
          `${CONFIG.apiUrl}/autocomplete/${CONFIG.store}?q=${encodeURIComponent(query)}&limit=6`,
          { signal: sig }
        );
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        return await resp.json();
      } catch (e) {
        if (e.name === 'AbortError') return null; // cancelled by a newer keystroke, not an error
        console.warn('WananSearch: autocomplete error', e);
        return { hits: [] };
      }
    }
  };

  // =========================================================================
  // State Management
  // =========================================================================

  const state = {
    buyerFactor: null,   // §3.4.2-bis: fattore del listino del buyer, arriva col JSON del proxy
    q: '',
    filters: {},       // { vendor: ['Gucci'], macro_category: ['Bags'], ... }
    sort: 'newest',
    offset: 0,
    results: null,
    loading: false,
    totalHits: 0,
    _abortController: null,  // for cancelling in-flight searches
  };

  function getActiveFilters() {
    const active = [];
    for (const [key, values] of Object.entries(state.filters)) {
      if (!values || !values.length) continue;
      for (const v of values) {
        active.push({ key, value: v });
      }
    }
    return active;
  }

  function setFilter(key, value, add = true) {
    if (!state.filters[key]) state.filters[key] = [];
    if (add) {
      if (!state.filters[key].includes(value)) {
        state.filters[key].push(value);
      }
    } else {
      state.filters[key] = state.filters[key].filter(v => v !== value);
      if (!state.filters[key].length) delete state.filters[key];
      // Clear child category filters when parent is removed
      if (key === 'macro_category' && !state.filters.macro_category) {
        delete state.filters.sub_category;
        delete state.filters.detail_category;
      } else if (key === 'sub_category' && !state.filters.sub_category) {
        delete state.filters.detail_category;
      }
    }
    state.offset = 0;
  }

  function clearFilters() {
    state.filters = {};
    state.offset = 0;
  }

  function buildSearchParams() {
    const params = { limit: CONFIG.pageSize, offset: state.offset };
    if (state.q) params.q = state.q;
    if (state.sort) params.sort = state.sort;
    if (CONFIG.collectionHandle) params.collection = CONFIG.collectionHandle;
    // Auto-filter gender from URL tag
    const tagGender = getGenderFromTags();
    if (tagGender && !state.filters.gender?.length) params.gender = tagGender;

    for (const [key, values] of Object.entries(state.filters)) {
      if (values && values.length) {
        params[key] = values;
      }
    }
    return params;
  }

  // URL <-> state sync
  function stateToUrl(pushNew = false) {
    const url = new URL(window.location);
    url.searchParams.delete('q');
    url.searchParams.delete('sort');
    url.searchParams.delete('page');
    for (const k of ['vendor', 'macro_category', 'sub_category', 'detail_category', 'gender', 'color', 'sizes', 'price_min', 'price_max']) {
      url.searchParams.delete(k);
    }

    if (state.q) url.searchParams.set('q', state.q);
    if (state.sort) url.searchParams.set('sort', state.sort);
    // Persist current page so back-navigation (e.g. returning from a product page)
    // lands on the same page instead of resetting to page 1.
    const pageNum = Math.floor(state.offset / CONFIG.pageSize) + 1;
    if (pageNum > 1) url.searchParams.set('page', String(pageNum));
    for (const [key, values] of Object.entries(state.filters)) {
      if (values && values.length) {
        url.searchParams.set(key, values.join(','));
      }
    }
    const urlStr = url.toString();
    if (pushNew && urlStr !== window.location.href) {
      window.history.pushState(null, '', urlStr);
    } else {
      window.history.replaceState(null, '', urlStr);
    }
  }

  function urlToState() {
    const url = new URL(window.location);
    state.q = url.searchParams.get('q') || '';
    state.sort = url.searchParams.get('sort') || 'newest';
    const pageNum = parseInt(url.searchParams.get('page'), 10);
    state.offset = pageNum > 1 ? (pageNum - 1) * CONFIG.pageSize : 0;
    state.filters = {};
    for (const k of ['vendor', 'macro_category', 'sub_category', 'detail_category', 'gender', 'color', 'sizes']) {
      const v = url.searchParams.get(k);
      if (v) state.filters[k] = v.split(',');
    }
    const pmin = url.searchParams.get('price_min');
    const pmax = url.searchParams.get('price_max');
    if (pmin) state.filters.price_min = [pmin];
    if (pmax) state.filters.price_max = [pmax];
  }

  // =========================================================================
  // Rendering — Product Grid
  // =========================================================================

  function imgSrc(url, width) {
    if (!url) return '';
    const sep = url.includes('?') ? '&' : '?';
    return escapeHtml(url) + sep + 'width=' + width;
  }

  // BF26: i soli tag bf26* dell'hit (se l'indice li espone), per le etichette di assets/bf26.js
  function bf26Tags(hit) {
    const t = Array.isArray(hit.tags) ? hit.tags : String(hit.tags || '').split(',');
    return t.map(x => String(x).trim().toLowerCase()).filter(x => x.indexOf('bf26') >= 0).join(' ');
  }

  function renderProductCard(hit) {
    const images = hit.image_urls || (hit.image_url ? [hit.image_url] : []);
    const hasMultiple = images.length > 1;
    const onSale = CONFIG.showPriceComparison && hit.compare_at_price && hit.compare_at_price > hit.price;
    const buyerHtml = buyerPriceHtml(hit);

    return `
      <a href="${escapeHtml(hit.url)}" class="ws-product-card" data-id="${escapeHtml(hit.shopify_id)}" data-bf26-tags="${escapeHtml(bf26Tags(hit))}">
        <div class="ws-product-image-wrap">
          <div class="ws-product-image-ratio" data-images='${JSON.stringify(images.map(u => escapeHtml(u)))}' data-index="0">
            ${images[0]
              ? `<img class="ws-product-img" src="${imgSrc(images[0], 600)}" alt="${escapeHtml(hit.title)}" loading="lazy" />`
              : '<div class="ws-product-no-image"></div>'
            }
          </div>
          ${hasMultiple ? `
            <button class="ws-carousel-arrow ws-carousel-prev" type="button" aria-label="Previous">&#8249;</button>
            <button class="ws-carousel-arrow ws-carousel-next" type="button" aria-label="Next">&#8250;</button>
          ` : ''}
          <button class="ws-wishlist-btn" type="button" aria-label="Wishlist">&#9825;</button>
        </div>
        <div class="ws-product-info">
          <div class="ws-product-meta">
            <span class="ws-product-vendor">${escapeHtml(hit.vendor)}</span>
            <span class="ws-product-price">
              ${buyerHtml ? buyerHtml : `${onSale ? `<span class="ws-price-compare">${formatPrice(hit.compare_at_price)}</span> ` : ''}
              <span class="${onSale ? 'ws-price-sale' : ''}">${formatPrice(hit.price)}</span>`}
            </span>
          </div>
          <div class="ws-product-title">${escapeHtml(hit.title)}</div>
        </div>
      </a>
    `;
  }

  function renderGrid(hits) {
    const grid = document.querySelector('.ws-product-grid');
    if (!grid) return;
    grid.innerHTML = hits.map(renderProductCard).join('');
    // iOS Safari sometimes fails to re-run CSS Grid track sizing after an
    // innerHTML swap, leaving column 1 vertically offset from column 2 on
    // paginated pages (page 2+). Force a synchronous relayout to fix it.
    // No visible flicker: there is no paint between the two display writes.
    grid.style.display = 'none';
    void grid.offsetHeight; // force reflow
    grid.style.display = '';
  }

  function bindCarousel() {
    document.querySelector('.ws-product-grid')?.addEventListener('click', (e) => {
      const arrow = e.target.closest('.ws-carousel-arrow');
      if (!arrow) return;
      e.preventDefault();
      e.stopPropagation();

      const ratio = arrow.closest('.ws-product-image-wrap').querySelector('.ws-product-image-ratio');
      const images = JSON.parse(ratio.dataset.images || '[]');
      if (images.length < 2) return;

      let idx = parseInt(ratio.dataset.index) || 0;
      idx = arrow.classList.contains('ws-carousel-next')
        ? (idx + 1) % images.length
        : (idx - 1 + images.length) % images.length;

      ratio.dataset.index = idx;
      const img = ratio.querySelector('.ws-product-img');
      if (img) img.src = imgSrc(images[idx], 600);
    });
  }

  function renderResultCount(total) {
    const label = wsT('results');
    const text = `${total.toLocaleString()} ${label}`;
    document.querySelectorAll('.ws-result-count').forEach(el => {
      el.textContent = text;
    });
  }

  // =========================================================================
  // Rendering — Filters (Horizontal Filter Bar)
  // =========================================================================

  const FACET_LABELS = {
    vendor: { en: 'Brand', it: 'Brand', fr: 'Marque', de: 'Marke', es: 'Marca' },
    macro_category: { en: 'Category', it: 'Categoria', fr: 'Cat\u00e9gorie', de: 'Kategorie', es: 'Categor\u00eda' },
    sub_category: { en: 'Subcategory', it: 'Sottocategoria', fr: 'Sous-cat\u00e9gorie', de: 'Unterkategorie', es: 'Subcategor\u00eda' },
    detail_category: { en: 'Detail', it: 'Dettaglio', fr: 'D\u00e9tail', de: 'Detail', es: 'Detalle' },
    gender: { en: 'Gender', it: 'Genere', fr: 'Genre', de: 'Geschlecht', es: 'G\u00e9nero' },
    color: { en: 'Color', it: 'Colore', fr: 'Couleur', de: 'Farbe', es: 'Color' },
    sizes: { en: 'Size', it: 'Taglia', fr: 'Taille', de: 'Gr\u00f6\u00dfe', es: 'Talla' },
  };

  const WS_UI_I18N = {
    it: { results: 'risultati', newest: 'Novit\u00e0', price_asc: 'Prezzo: crescente', price_desc: 'Prezzo: decrescente', search_ph: 'Cerca...', price: 'Prezzo', sort_by: 'Ordina Per', show_results: 'Mostra risultati', no_results: 'Nessun risultato' },
    en: { results: 'results', newest: 'Newest', price_asc: 'Price: Low to High', price_desc: 'Price: High to Low', search_ph: 'Search...', price: 'Price', sort_by: 'Sort By', show_results: 'Show results', no_results: 'No results' },
    fr: { results: 'r\u00e9sultats', newest: 'Nouveaut\u00e9s', price_asc: 'Prix : croissant', price_desc: 'Prix : d\u00e9croissant', search_ph: 'Rechercher...', price: 'Prix', sort_by: 'Trier par', show_results: 'Afficher les r\u00e9sultats', no_results: 'Aucun r\u00e9sultat' },
    de: { results: 'Ergebnisse', newest: 'Neuheiten', price_asc: 'Preis: aufsteigend', price_desc: 'Preis: absteigend', search_ph: 'Suchen...', price: 'Preis', sort_by: 'Sortieren nach', show_results: 'Ergebnisse anzeigen', no_results: 'Keine Ergebnisse' },
    es: { results: 'resultados', newest: 'Novedades', price_asc: 'Precio: ascendente', price_desc: 'Precio: descendente', search_ph: 'Buscar...', price: 'Precio', sort_by: 'Ordenar por', show_results: 'Mostrar resultados', no_results: 'Sin resultados' },
  };
  function wsT(key) {
    const t = WS_UI_I18N[CONFIG.locale] || WS_UI_I18N.en;
    return t[key] !== undefined ? t[key] : WS_UI_I18N.en[key];
  }

  function getFacetLabel(key) {
    const labels = FACET_LABELS[key];
    return labels ? (labels[CONFIG.locale] || labels.en) : key;
  }

  // Context-aware facet visibility — driven by facet distribution
  // If a facet has only 1 value in the collection, it's redundant → hide it
  // sub_category shown only when macro is single or selected
  // detail_category shown only for specific subcategories
  const DETAIL_SUBCATEGORIES = ['coats', 'jackets', 'topwear'];

  // Stores the initial (unfiltered) facet distribution for the collection
  let _collectionFacetSnapshot = null;

  function setCollectionFacetSnapshot(facetDistribution) {
    // Only set once per page load (the first search = unfiltered collection)
    if (!_collectionFacetSnapshot && facetDistribution) {
      _collectionFacetSnapshot = facetDistribution;
    }
  }

  function getFacetValueCount(key) {
    // Use snapshot (unfiltered) to determine collection-level facet cardinality
    const dist = (_collectionFacetSnapshot || {})[key];
    return dist ? Object.keys(dist).length : 0;
  }

  function shouldShowFacet(key, currentDistribution) {
    const dist = currentDistribution || {};

    // If only 1 value exists in the entire collection → filter is useless
    if (getFacetValueCount(key) <= 1) return false;

    // Gender: hide if handle/title OR current tags imply gender
    if (key === 'gender') {
      const genderWords = ['donna', 'women', 'woman', 'womens', "women's", 'uomo', 'men', 'man', 'mens', "men's"];
      const h = (CONFIG.collectionHandle + ' ' + CONFIG.collectionTitle).toLowerCase();
      if (genderWords.some(k => h.includes(k))) return false;
      // Check URL tags (e.g., /collections/shoes/uomo)
      const tags = (CONFIG.currentTags || []).map(t => t.toLowerCase());
      if (tags.some(t => genderWords.includes(t))) return false;
    }

    // Category: if collection has only 1 macro_category, skip it and show sub directly
    if (key === 'macro_category' && getFacetValueCount('macro_category') <= 1) return false;

    // Subcategory: show if macro has single value (collection-level) OR user selected a macro
    if (key === 'sub_category') {
      const macroSingle = getFacetValueCount('macro_category') <= 1;
      const macroSelected = !!(state.filters.macro_category?.length);
      return macroSingle || macroSelected;
    }

    // Detail: only for specific subcategories
    if (key === 'detail_category') {
      const subs = state.filters.sub_category || [];
      // Also check if collection is scoped to a single sub that qualifies
      const snapshotSubs = (_collectionFacetSnapshot || {}).sub_category || {};
      const snapshotSubKeys = Object.keys(snapshotSubs);
      const allSubs = subs.length ? subs : (snapshotSubKeys.length === 1 ? snapshotSubKeys : []);
      return allSubs.length > 0 && allSubs.some(s => DETAIL_SUBCATEGORIES.includes(s.toLowerCase()));
    }

    return true;
  }

  function renderSortDropdown(label) {
    const options = [
      { value: 'newest', label: wsT('newest') },
      { value: 'featured', label: 'Featured' },
      { value: 'price_asc', label: wsT('price_asc') },
      { value: 'price_desc', label: wsT('price_desc') },
      { value: 'vendor_asc', label: 'Brand A-Z' },
    ];
    const currentLabel = options.find(o => o.value === state.sort)?.label || options[0].label;
    return `
      <div class="ws-filter-drop">
        <button class="ws-filter-btn" type="button">
          ${escapeHtml(currentLabel)}
          <svg width="10" height="6" viewBox="0 0 10 6"><polyline points="1,1 5,5 9,1" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
        </button>
        <div class="ws-filter-dropdown">
          <ul class="ws-facet-list">
            ${options.map(o => `
              <li class="ws-facet-item" data-sort-value="${o.value}">
                <label>
                  <input type="radio" name="ws-sort" ${state.sort === o.value ? 'checked' : ''} />
                  <span class="ws-facet-label">${escapeHtml(o.label)}</span>
                </label>
              </li>
            `).join('')}
          </ul>
        </div>
      </div>
    `;
  }

  // Custom sort order for macro_category (EN + IT)
  const CATEGORY_ORDER = [
    'clothing', 'abbigliamento',
    'shoes', 'scarpe',
    'bags', 'borse',
    'accessories', 'accessori',
    'jewellery', 'gioielli',
    'home & lifestyle', 'casa e lifestyle',
    'beauty',
  ];

  const SIZE_ORDER = ['xxxs', 'xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', '3xl', '4xl', 'os'];
  // Filtro taglie (D1821): numeri nudi, poi le sigle in quest'ordine, ciascuna numerica. Prima `IT 40` era NaN
  // e finiva in coda dopo le lettere. Sulle scarpe l'indice ha gia' fuso IT/EU nel numero nudo.
  const SIZE_SYSTEMS = ['IT', 'EU', 'FR', 'DE', 'UK', 'US', 'JP'];
  function sizeKey(v) {
    const s = String(v).trim().replace(',', '.');
    if (/^\d+(\.\d+)?$/.test(s)) return [0, parseFloat(s)];
    const m = s.match(/^([A-Za-z]{2})\s*(\d+(?:\.\d+)?)$/);
    const i = m ? SIZE_SYSTEMS.indexOf(m[1].toUpperCase()) : -1;
    return i >= 0 ? [i + 1, parseFloat(m[2])] : null;
  }
  function compareSizes(a, b) {
    const ka = sizeKey(a), kb = sizeKey(b);
    if (ka && kb) return (ka[0] - kb[0]) || (ka[1] - kb[1]); // system, then numeric asc
    if (!!ka !== !!kb) return ka ? -1 : 1;      // numeric (bare or with system) before alpha
    const ia = SIZE_ORDER.indexOf(a.toLowerCase());
    const ib = SIZE_ORDER.indexOf(b.toLowerCase());
    if (ia >= 0 && ib >= 0) return ia - ib;     // both canonical alpha → canonical order
    if (ia >= 0 !== (ib >= 0)) return ia >= 0 ? -1 : 1; // canonical before unknown
    return a.localeCompare(b);                  // fallback
  }

  // Valori a 0 nascosti in tutti i facet (Virgilio 18/10): lo zero nasce dal merge con lo snapshot della collection.
  // Un valore selezionato resta visibile anche a 0, altrimenti non si potrebbe deselezionarlo.
  function visibleFacetEntries(merged, selectedValues) {
    return Object.entries(merged).filter(([v, c]) => c > 0 || selectedValues.includes(v));
  }

  function sortFacetEntries(key, entries, selectedValues) {
    return entries.sort((a, b) => {
      // Selected items always first
      const aSelected = selectedValues.includes(a[0]);
      const bSelected = selectedValues.includes(b[0]);
      if (aSelected !== bSelected) return aSelected ? -1 : 1;
      // Custom order for macro_category
      if (key === 'macro_category') {
        const aIdx = CATEGORY_ORDER.indexOf(a[0].toLowerCase());
        const bIdx = CATEGORY_ORDER.indexOf(b[0].toLowerCase());
        const aPos = aIdx >= 0 ? aIdx : 999;
        const bPos = bIdx >= 0 ? bIdx : 999;
        return aPos - bPos;
      }
      if (key === 'sizes') {
        return compareSizes(a[0], b[0]);
      }
      return a[0].localeCompare(b[0]);
    });
  }

  function renderFilterDropdown(key, distribution) {
    if (!distribution || !Object.keys(distribution).length) return '';
    const selectedValues = state.filters[key] || [];

    // Merge snapshot (all values) with current distribution (filtered counts).
    // This ensures all facet values remain visible even when filters are active.
    const snapshotDist = (_collectionFacetSnapshot || {})[key] || {};
    const merged = {};
    for (const [v, c] of Object.entries(snapshotDist)) { merged[v] = 0; }
    for (const [v, c] of Object.entries(distribution)) { merged[v] = c; }
    // Keep selected values visible even if they disappeared from both distributions
    for (const v of selectedValues) { if (!(v in merged)) merged[v] = 0; }

    const entries = sortFacetEntries(key, visibleFacetEntries(merged, selectedValues), selectedValues);
    const hasSearch = entries.length > 10;
    const hasActive = selectedValues.length > 0;
    return `
      <div class="ws-filter-drop ${hasActive ? 'ws-drop--active' : ''}" data-facet="${key}">
        <button class="ws-filter-btn" type="button">
          ${getFacetLabel(key)}${hasActive ? ` (${selectedValues.length})` : ''}
          <svg width="10" height="6" viewBox="0 0 10 6"><polyline points="1,1 5,5 9,1" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
        </button>
        <div class="ws-filter-dropdown">
          ${hasSearch ? `<input type="text" class="ws-facet-search" placeholder="${wsT('search_ph')}" />` : ''}
          <ul class="ws-facet-list">
            ${entries.map(([value, count]) => {
              const checked = selectedValues.includes(value);
              return `
                <li class="ws-facet-item${count === 0 && !checked ? ' ws-facet-item--empty' : ''}" data-value="${escapeHtml(value)}">
                  <label>
                    <input type="checkbox" ${checked ? 'checked' : ''} />
                    <span class="ws-facet-label">${escapeHtml(formatFacetValue(key, value))}</span>
                    <span class="ws-facet-count">(${count})</span>
                  </label>
                </li>
              `;
            }).join('')}
          </ul>
        </div>
      </div>
    `;
  }

  // Render the API-provided price ranges as clickable presets (shared desktop/mobile).
  function renderPricePresets(activeMin, activeMax) {
    const ranges = (state.results && state.results.priceRanges) || [];
    if (!ranges.length) return '';
    return `<ul class="ws-price-presets">` + ranges.map(([rmin, rmax, rlabel]) => {
      const isActive = String(activeMin || '') === String(rmin ?? '') &&
                       String(activeMax || '') === String(rmax ?? '');
      return `<li>
        <button type="button" class="ws-price-preset${isActive ? ' ws-price-preset--active' : ''}"
                data-min="${rmin ?? ''}" data-max="${rmax ?? ''}">${escapeHtml(rlabel)}</button>
      </li>`;
    }).join('') + `</ul>`;
  }
  // Bind preset clicks within a container (shared desktop/mobile).
  function bindPricePresets(container, keepOpen) {
    container.querySelectorAll('.ws-price-preset').forEach(btn => {
      btn.addEventListener('click', () => {
        const min = btn.dataset.min || '';
        const max = btn.dataset.max || '';
        if (min) { state.filters.price_min = [min]; } else { delete state.filters.price_min; }
        if (max) { state.filters.price_max = [max]; } else { delete state.filters.price_max; }
        state.offset = 0;
        if (keepOpen) _openDropdownFacet = 'price';
        executeSearch();
      });
    });
  }

  function renderPriceDropdown() {
    const label = wsT('price');
    const symbol = CONFIG.currencySymbol;
    const pmin = (state.filters.price_min || [''])[0];
    const pmax = (state.filters.price_max || [''])[0];
    const hasActive = !!(pmin || pmax);
    return `
      <div class="ws-filter-drop ${hasActive ? 'ws-drop--active' : ''}" data-facet="price">
        <button class="ws-filter-btn" type="button">
          ${label} (${symbol})${hasActive ? ' *' : ''}
          <svg width="10" height="6" viewBox="0 0 10 6"><polyline points="1,1 5,5 9,1" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
        </button>
        <div class="ws-filter-dropdown">
          ${renderPricePresets(pmin, pmax)}
          <div class="ws-price-inputs">
            <div class="ws-price-field">
              <span>${symbol}</span>
              <input type="number" class="ws-price-min" placeholder="Min" value="${pmin}" min="0" step="50" />
            </div>
            <span class="ws-price-separator">-</span>
            <div class="ws-price-field">
              <span>${symbol}</span>
              <input type="number" class="ws-price-max" placeholder="Max" value="${pmax}" min="0" step="50" />
            </div>
          </div>
        </div>
      </div>
    `;
  }

  // Track which dropdown is currently open so we can restore it after re-render
  let _openDropdownFacet = null;

  function renderFilterBar(facetDistribution) {
    // Save initial snapshot for context-aware decisions
    setCollectionFacetSnapshot(facetDistribution);

    // Left side: facet filter dropdowns
    const container = document.getElementById('wsFilterDropdowns');
    if (!container) return;

    // Remember which dropdown was open before re-render
    const openFacet = _openDropdownFacet ||
      container.querySelector('.ws-filter-drop.ws-drop--open')?.dataset?.facet || null;

    let html = '';
    const facetOrder = ['gender', 'macro_category', 'sub_category', 'detail_category', 'vendor', 'color', 'sizes'];
    for (const key of facetOrder) {
      if (!shouldShowFacet(key, facetDistribution)) continue;
      const dist = facetDistribution[key];
      // Use snapshot to decide visibility (filtered distribution may collapse to 1 value)
      const snapshotCount = getFacetValueCount(key);
      if (dist && (Object.keys(dist).length > 1 || snapshotCount > 1)) {
        html += renderFilterDropdown(key, dist);
      }
    }
    html += renderPriceDropdown();
    container.innerHTML = html;
    bindFilterBarEvents(container);

    // Restore the dropdown that was open before re-render
    if (openFacet) {
      const drop = container.querySelector(`.ws-filter-drop[data-facet="${openFacet}"]`);
      if (drop) drop.classList.add('ws-drop--open');
    }

    // Right side: sort dropdown
    const sortContainer = document.getElementById('wsSortDropdown');
    if (sortContainer) {
      const sortLabel = wsT('sort_by');
      sortContainer.innerHTML = renderSortDropdown(sortLabel);
      bindFilterBarEvents(sortContainer);
    }
  }

  function bindFilterBarEvents(container) {
    // Toggle dropdown popovers — only one open at a time
    container.querySelectorAll('.ws-filter-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const drop = btn.closest('.ws-filter-drop');
        const wasOpen = drop.classList.contains('ws-drop--open');
        container.querySelectorAll('.ws-filter-drop').forEach(d => d.classList.remove('ws-drop--open'));
        if (!wasOpen) {
          drop.classList.add('ws-drop--open');
          _openDropdownFacet = drop.dataset?.facet || null;
        } else {
          _openDropdownFacet = null;
        }
      });
    });

    // Click outside closes all — use single global listener (set in init, not here)
    // Handled by _globalClickCloseDropdowns in init()

    // Prevent dropdown content from closing on click
    container.querySelectorAll('.ws-filter-dropdown').forEach(dd => {
      dd.addEventListener('click', (e) => e.stopPropagation());
    });

    // Sort radio buttons
    container.querySelectorAll('[data-sort-value]').forEach(item => {
      item.querySelector('input')?.addEventListener('change', () => {
        state.sort = item.dataset.sortValue;
        state.offset = 0;
        executeSearch();
      });
    });

    // Facet checkboxes
    container.querySelectorAll('.ws-filter-drop[data-facet]').forEach(drop => {
      const key = drop.dataset.facet;
      if (key === 'price') return;

      drop.querySelectorAll('.ws-facet-item input[type="checkbox"]').forEach(cb => {
        cb.addEventListener('change', () => {
          const value = cb.closest('.ws-facet-item').dataset.value;
          // Keep dropdown open during re-render for multi-select
          _openDropdownFacet = key;
          setFilter(key, value, cb.checked);
          executeSearch();
        });
      });
    });

    // Facet search
    container.querySelectorAll('.ws-facet-search').forEach(input => {
      input.addEventListener('input', () => {
        const query = input.value.toLowerCase();
        const list = input.closest('.ws-filter-dropdown').querySelector('.ws-facet-list');
        list?.querySelectorAll('.ws-facet-item').forEach(item => {
          const label = item.dataset.value?.toLowerCase() || '';
          item.style.display = label.includes(query) ? '' : 'none';
        });
      });
    });

    // Price inputs
    const priceDebounce = debounce(() => {
      const minInput = container.querySelector('.ws-price-min');
      const maxInput = container.querySelector('.ws-price-max');
      const min = minInput?.value || '';
      const max = maxInput?.value || '';
      if (min) { state.filters.price_min = [min]; } else { delete state.filters.price_min; }
      if (max) { state.filters.price_max = [max]; } else { delete state.filters.price_max; }
      state.offset = 0;
      executeSearch();
    }, 600);

    container.querySelectorAll('.ws-price-min, .ws-price-max').forEach(input => {
      input.addEventListener('input', priceDebounce);
    });
    bindPricePresets(container, false);
  }

  // =========================================================================
  // Active Filter Chips
  // =========================================================================

  function renderActiveChips() {
    const container = document.querySelector('.ws-active-filters');
    if (!container) return;

    const active = getActiveFilters().filter(f => f.key !== 'price_min' && f.key !== 'price_max');
    if (!active.length) {
      container.innerHTML = '';
      return;
    }

    container.innerHTML = active.map(f => `
      <button class="ws-chip" data-key="${f.key}" data-value="${escapeHtml(f.value)}">
        ${escapeHtml(formatFacetValue(f.key, f.value))}
        <svg width="10" height="10" viewBox="0 0 10 10">
          <line x1="2" y1="2" x2="8" y2="8" stroke="white" stroke-width="1.5"/>
          <line x1="8" y1="2" x2="2" y2="8" stroke="white" stroke-width="1.5"/>
        </svg>
      </button>
    `).join('');

    container.querySelectorAll('.ws-chip').forEach(chip => {
      chip.addEventListener('click', () => {
        setFilter(chip.dataset.key, chip.dataset.value, false);
        executeSearch();
      });
    });
  }

  // =========================================================================
  // Mobile Filter Drawer
  // =========================================================================


  function bindMobileDrawer() {
    const trigger = document.querySelector('.ws-mobile-filter-btn');
    const drawer = document.querySelector('.ws-mobile-drawer');
    if (!trigger || !drawer) return;

    trigger.addEventListener('click', () => {
      drawer.classList.add('ws-drawer--open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('ws-no-scroll');
    });

    const closeDrawer = () => {
      drawer.classList.remove('ws-drawer--open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ws-no-scroll');
    };

    drawer.querySelector('.ws-drawer-close')?.addEventListener('click', closeDrawer);
    drawer.querySelector('.ws-drawer-clear')?.addEventListener('click', () => {
      clearFilters();
      executeSearch();
      closeDrawer();
    });
    drawer.querySelector('.ws-drawer-apply')?.addEventListener('click', closeDrawer);

    // Close on overlay click
    drawer.querySelector('.ws-drawer-overlay')?.addEventListener('click', closeDrawer);
    drawer.addEventListener('click', (e) => {
      if (e.target === drawer) closeDrawer();
    });
  }

  function renderMobileFacet(key, distribution) {
    if (!distribution || !Object.keys(distribution).length) return '';
    const selectedValues = state.filters[key] || [];

    // Merge snapshot with current distribution for multi-select visibility
    const snapshotDist = (_collectionFacetSnapshot || {})[key] || {};
    const merged = {};
    for (const [v, c] of Object.entries(snapshotDist)) { merged[v] = 0; }
    for (const [v, c] of Object.entries(distribution)) { merged[v] = c; }
    for (const v of selectedValues) { if (!(v in merged)) merged[v] = 0; }

    const entries = sortFacetEntries(key, visibleFacetEntries(merged, selectedValues), selectedValues);
    const hasSearch = entries.length > 10;
    const isOpen = selectedValues.length > 0;
    const subtitle = selectedValues.length ? selectedValues.map(v => formatFacetValue(key, v)).join(', ') : '';

    return `
      <div class="ws-mobile-facet ${isOpen ? 'ws-mobile-facet--open' : ''}" data-facet="${key}">
        <button class="ws-mobile-facet-header" type="button">
          <div>
            <span class="ws-mobile-facet-title">${getFacetLabel(key)}</span>
            ${subtitle ? `<span class="ws-mobile-facet-subtitle">${escapeHtml(subtitle)}</span>` : ''}
          </div>
          <svg class="ws-mobile-facet-arrow" width="8" height="14" viewBox="0 0 8 14"><polyline points="1,1 7,7 1,13" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
        </button>
        <div class="ws-mobile-facet-body">
          ${hasSearch ? `<input type="text" class="ws-facet-search" placeholder="${wsT('search_ph')}" />` : ''}
          <ul class="ws-facet-list">
            ${entries.map(([value, count]) => {
              const checked = selectedValues.includes(value);
              return `
              <li class="ws-facet-item${count === 0 && !checked ? ' ws-facet-item--empty' : ''}" data-value="${escapeHtml(value)}">
                <label>
                  <input type="checkbox" ${checked ? 'checked' : ''} />
                  <span class="ws-facet-label">${escapeHtml(formatFacetValue(key, value))}</span>
                  <span class="ws-facet-count">(${count})</span>
                </label>
              </li>
            `}).join('')}
          </ul>
        </div>
      </div>
    `;
  }

  function renderMobilePriceFacet() {
    const label = wsT('price');
    const symbol = CONFIG.currencySymbol;
    const pmin = (state.filters.price_min || [''])[0];
    const pmax = (state.filters.price_max || [''])[0];
    const isOpen = !!(pmin || pmax);
    return `
      <div class="ws-mobile-facet ${isOpen ? 'ws-mobile-facet--open' : ''}" data-facet="price">
        <button class="ws-mobile-facet-header" type="button">
          <div><span class="ws-mobile-facet-title">${label} (${symbol})</span></div>
          <svg class="ws-mobile-facet-arrow" width="8" height="14" viewBox="0 0 8 14"><polyline points="1,1 7,7 1,13" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
        </button>
        <div class="ws-mobile-facet-body">
          ${renderPricePresets(pmin, pmax)}
          <div class="ws-price-inputs">
            <div class="ws-price-field"><span>${symbol}</span><input type="number" class="ws-price-min" placeholder="Min" value="${pmin}" min="0" step="50" /></div>
            <span class="ws-price-separator">-</span>
            <div class="ws-price-field"><span>${symbol}</span><input type="number" class="ws-price-max" placeholder="Max" value="${pmax}" min="0" step="50" /></div>
          </div>
        </div>
      </div>
    `;
  }

  function bindMobileFacetEvents(container) {
    // Accordion toggle
    container.querySelectorAll('.ws-mobile-facet-header').forEach(btn => {
      btn.addEventListener('click', () => {
        btn.closest('.ws-mobile-facet').classList.toggle('ws-mobile-facet--open');
      });
    });

    // Checkboxes
    container.querySelectorAll('.ws-mobile-facet[data-facet]').forEach(facet => {
      const key = facet.dataset.facet;
      if (key === 'price') return;
      facet.querySelectorAll('.ws-facet-item input[type="checkbox"]').forEach(cb => {
        cb.addEventListener('change', () => {
          const value = cb.closest('.ws-facet-item').dataset.value;
          // Keep accordion open during re-render for multi-select
          _openDropdownFacet = key;
          setFilter(key, value, cb.checked);
          executeSearch();
        });
      });
    });

    // Facet search
    container.querySelectorAll('.ws-facet-search').forEach(input => {
      input.addEventListener('input', () => {
        const query = input.value.toLowerCase();
        const list = input.closest('.ws-mobile-facet-body').querySelector('.ws-facet-list');
        list?.querySelectorAll('.ws-facet-item').forEach(item => {
          const label = item.dataset.value?.toLowerCase() || '';
          item.style.display = label.includes(query) ? '' : 'none';
        });
      });
    });

    // Price
    const priceDb = debounce(() => {
      const min = container.querySelector('.ws-price-min')?.value || '';
      const max = container.querySelector('.ws-price-max')?.value || '';
      if (min) { state.filters.price_min = [min]; } else { delete state.filters.price_min; }
      if (max) { state.filters.price_max = [max]; } else { delete state.filters.price_max; }
      state.offset = 0;
      executeSearch();
    }, 600);
    container.querySelectorAll('.ws-price-min, .ws-price-max').forEach(input => {
      input.addEventListener('input', priceDb);
    });
    bindPricePresets(container, true);
  }

  function renderMobileSortFacet() {
    const sortLabel = wsT('sort_by');
    const options = [
      { value: 'newest', label: wsT('newest') },
      { value: 'featured', label: 'Featured' },
      { value: 'price_asc', label: wsT('price_asc') },
      { value: 'price_desc', label: wsT('price_desc') },
      { value: 'vendor_asc', label: 'Brand A-Z' },
    ];
    const currentLabel = options.find(o => o.value === state.sort)?.label || options[0].label;
    return `
      <div class="ws-mobile-facet" data-facet="sort">
        <button class="ws-mobile-facet-header" type="button">
          <div>
            <span class="ws-mobile-facet-title">${sortLabel}</span>
            <span class="ws-mobile-facet-subtitle">${escapeHtml(currentLabel)}</span>
          </div>
          <svg class="ws-mobile-facet-arrow" width="8" height="14" viewBox="0 0 8 14"><polyline points="1,1 7,7 1,13" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>
        </button>
        <div class="ws-mobile-facet-body">
          <ul class="ws-facet-list">
            ${options.map(o => `
              <li class="ws-facet-item" data-sort-value="${o.value}">
                <label>
                  <input type="radio" name="ws-mobile-sort" ${state.sort === o.value ? 'checked' : ''} />
                  <span class="ws-facet-label">${escapeHtml(o.label)}</span>
                </label>
              </li>
            `).join('')}
          </ul>
        </div>
      </div>
    `;
  }

  function updateMobileDrawer(facetDistribution) {
    const drawerContent = document.querySelector('.ws-drawer-facets');
    if (!drawerContent) return;

    // Remember which mobile facet accordion was open
    const openMobileFacet = _openDropdownFacet ||
      drawerContent.querySelector('.ws-mobile-facet--open[data-facet]')?.dataset?.facet || null;

    // Sort first, then filters
    let html = renderMobileSortFacet();

    const facetOrder = ['gender', 'macro_category', 'sub_category', 'detail_category', 'vendor', 'color', 'sizes'];
    for (const key of facetOrder) {
      if (!shouldShowFacet(key, facetDistribution)) continue;
      const dist = facetDistribution[key];
      const snapshotCount = getFacetValueCount(key);
      if (dist && (Object.keys(dist).length > 1 || snapshotCount > 1)) {
        html += renderMobileFacet(key, dist);
      }
    }
    html += renderMobilePriceFacet();
    drawerContent.innerHTML = html;
    bindMobileFacetEvents(drawerContent);

    // Restore open accordion after re-render
    if (openMobileFacet) {
      const facet = drawerContent.querySelector(`.ws-mobile-facet[data-facet="${openMobileFacet}"]`);
      if (facet) facet.classList.add('ws-mobile-facet--open');
    }

    // Bind sort radios in drawer
    drawerContent.querySelectorAll('[data-sort-value]').forEach(item => {
      item.querySelector('input')?.addEventListener('change', () => {
        state.sort = item.dataset.sortValue;
        state.offset = 0;
        executeSearch();
      });
    });

    const applyBtn = document.querySelector('.ws-drawer-apply');
    if (applyBtn) {
      const label = wsT('show_results');
      applyBtn.textContent = `${label} (${state.totalHits.toLocaleString()})`;
    }
  }

  // =========================================================================
  // Load More / Infinite Scroll
  // =========================================================================

  // =========================================================================
  // Pagination (numbered pages)
  // =========================================================================

  function renderPagination() {
    const nav = document.querySelector('.ws-pagination');
    if (!nav) return;

    const totalPages = Math.ceil(state.totalHits / CONFIG.pageSize);
    const currentPage = Math.floor(state.offset / CONFIG.pageSize) + 1;

    if (totalPages <= 1) {
      nav.innerHTML = '';
      return;
    }

    let html = '';

    // Format page number with leading zero: 1 → "01", 10 → "10"
    const fmt = (n) => String(n).padStart(2, '0');

    // Page numbers — show max 7 with ellipsis
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }

    for (const p of pages) {
      if (p === '...') {
        html += `<span class="ws-page-ellipsis">…</span>`;
      } else {
        html += `<button class="ws-page-btn ${p === currentPage ? 'ws-page-btn--active' : ''}" data-page="${p}">${fmt(p)}</button>`;
      }
    }

    nav.innerHTML = html;

    // Bind click events
    nav.querySelectorAll('.ws-page-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const page = parseInt(btn.dataset.page);
        if (!page || page === currentPage) return;
        state.offset = (page - 1) * CONFIG.pageSize;
        // Scroll to top immediately, then search
        window.scrollTo({ top: 0, behavior: 'instant' });
        executeSearch();
      });
    });
  }

  // =========================================================================
  // Autocomplete
  // =========================================================================

  function initAutocomplete() {
    let dropdown = null;
    let boundInput = null;
    let _acAbort = null; // AbortController for autocomplete

    function ensureDropdown(input) {
      if (dropdown && boundInput === input) return dropdown;
      if (dropdown) dropdown.remove();

      dropdown = document.createElement('div');
      dropdown.className = 'ws-search-dropdown';

      // Hide Shopify's native predictive search
      const native = document.getElementById('predictive-search');
      if (native) native.style.display = 'none';

      // Append to body as fixed overlay, positioned below the input
      document.body.appendChild(dropdown);

      boundInput = input;
      return dropdown;
    }

    let _posListening = false;
    function positionDropdown() {
      if (!dropdown || !boundInput) return;
      const rect = boundInput.getBoundingClientRect();
      dropdown.style.top = (rect.bottom) + 'px';
      dropdown.style.left = '0';
      dropdown.style.right = '0';
      // Start tracking scroll/resize while dropdown is open
      if (!_posListening) {
        window.addEventListener('scroll', positionDropdown, { passive: true });
        window.addEventListener('resize', positionDropdown, { passive: true });
        _posListening = true;
      }
    }
    function stopPositioning() {
      if (_posListening) {
        window.removeEventListener('scroll', positionDropdown);
        window.removeEventListener('resize', positionDropdown);
        _posListening = false;
      }
    }

    const doSearch = debounce(async (input) => {
      const dd = ensureDropdown(input);
      const q = input.value.trim();
      if (q.length < CONFIG.autocompleteMinChars) {
        stopPositioning(); dd.classList.remove('ws-search-dropdown--open');
        return;
      }

      // Cancel previous autocomplete request
      if (_acAbort) _acAbort.abort();
      _acAbort = new AbortController();
      const result = await api.autocomplete(q, _acAbort.signal);
      if (result === null) return; // aborted by a newer keystroke — the newer request will render
      if (!result.hits.length) {
        dd.innerHTML = `<div class="ws-search-empty">${wsT('no_results')}</div>`;
        positionDropdown(); dd.classList.add('ws-search-dropdown--open');
        return;
      }

      buyerFactorFrom(result);
      dd.innerHTML = result.hits.map(hit => {
        const rawHighlight = hit._formatted?.title || '';
        const highlighted = rawHighlight
          ? rawHighlight.replace(/<(?!\/?mark>)[^>]+>/gi, '')
          : escapeHtml(hit.title);
        return `
          <a href="${escapeHtml(hit.url)}" class="ws-search-item">
            ${hit.image_url ? `<img src="${imgSrc(hit.image_url, 80)}" alt="" loading="lazy" />` : ''}
            <div class="ws-search-item-info">
              <div class="ws-search-item-vendor">${escapeHtml(hit.vendor)}</div>
              <div class="ws-search-item-title">${highlighted}</div>
              <div class="ws-search-item-price">${buyerPriceHtml(hit) || formatPrice(hit.price)}</div>
            </div>
          </a>
        `;
      }).join('');
      positionDropdown(); dd.classList.add('ws-search-dropdown--open');
    }, CONFIG.debounceMs);

    // Use event delegation — catches inputs even if added dynamically
    document.addEventListener('input', (e) => {
      const input = e.target.closest('.search__input, #Search, input[name="q"]');
      if (!input) return;
      doSearch(input);
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      if (dropdown && !e.target.closest('.search__input-wrap') && !e.target.closest('.ws-search-dropdown') && !e.target.closest('.search__input')) {
        stopPositioning(); dropdown.classList.remove('ws-search-dropdown--open');
      }
    });

    // Intercept form submit
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form.hc-searchbar, form[action*="search"]');
      if (!form) return;
      const input = form.querySelector('.search__input, input[name="q"]');
      if (!input) return;
      e.preventDefault();
      if (dropdown) { stopPositioning(); dropdown.classList.remove('ws-search-dropdown--open'); }
      const q = input.value.trim();
      if (q) window.location.href = '/search?q=' + encodeURIComponent(q);
    });
  }

  // =========================================================================
  // Scroll position memory — restore the spot after returning from a product
  // =========================================================================

  let _didRestoreScroll = false;
  function scrollKey() { return 'wsScroll:' + location.pathname + location.search; }

  // Save scroll position when a product card is opened. Delegated on the grid
  // element, which persists across renderGrid() innerHTML swaps. The click fires
  // synchronously before the <a> navigates, so the write completes.
  function bindScrollMemory() {
    document.querySelector('.ws-product-grid')?.addEventListener('click', (e) => {
      const card = e.target.closest('.ws-product-card');
      if (!card) return;
      if (e.target.closest('.ws-carousel-arrow') || e.target.closest('.ws-wishlist-btn')) return;
      try { sessionStorage.setItem(scrollKey(), String(window.scrollY)); } catch (_) {}
    });
  }

  // After the first successful render following a (re)load, re-anchor to the
  // saved position (if the user came back from a product on this exact URL+page).
  function restoreScrollOnce() {
    if (_didRestoreScroll) return;
    _didRestoreScroll = true;
    try {
      const y = sessionStorage.getItem(scrollKey());
      if (y === null) return;
      sessionStorage.removeItem(scrollKey());
      requestAnimationFrame(() => window.scrollTo(0, parseInt(y, 10) || 0));
    } catch (_) {}
  }

  // =========================================================================
  // Main Search Execution
  // =========================================================================

  async function executeSearch() {
    // Cancel any in-flight search
    if (state._abortController) {
      state._abortController.abort();
    }
    state._abortController = new AbortController();
    const signal = state._abortController.signal;

    state.loading = true;
    const loadingEl = document.querySelector('.ws-loading');
    if (loadingEl) loadingEl.style.display = '';

    const params = buildSearchParams();
    const result = await api.search(params, signal);

    // If this request was aborted, a newer one is in flight — bail out
    if (signal.aborted) return;

    state.loading = false;
    state._abortController = null;
    if (loadingEl) loadingEl.style.display = 'none';

    if (!result) {
      // API failed (blocked/timeout/HTTP error). Bail WITHOUT touching the grid:
      // it holds the server-side fallback cards (see wanan-filters.liquid). Clearing
      // it here would re-create the blank-page bug for ad-blocker/slow-network users.
      console.warn('WananSearch: API unreachable, keeping server-side fallback grid');
      return;
    }

    state.results = result;
    state.totalHits = result.estimatedTotalHits || 0;
    buyerFactorFrom(result);

    // No results state
    const noResults = document.querySelector('.ws-no-results');
    if (noResults) {
      noResults.style.display = state.totalHits === 0 ? '' : 'none';
    }

    renderGrid(result.hits || []);
    renderResultCount(state.totalHits);
    renderActiveChips();
    renderPagination();
    renderFilterBar(result.facetDistribution || {});
    updateMobileDrawer(result.facetDistribution || {});

    const shouldPush = !state._isPopstate;
    state._isPopstate = false;
    stateToUrl(shouldPush);

    // First render after a (re)load: re-anchor to the product the user returned from.
    restoreScrollOnce();
  }

  // =========================================================================
  // Grid View Toggle
  // =========================================================================

  function bindGridToggle() {
    document.querySelectorAll('.ws-grid-toggle').forEach(btn => {
      btn.addEventListener('click', () => {
        const cols = btn.dataset.cols;
        const grid = document.querySelector('.ws-product-grid');
        if (!grid) return;
        document.querySelectorAll('.ws-grid-toggle').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        grid.dataset.cols = cols;
      });
    });
  }

  // =========================================================================
  // Init
  // =========================================================================

  function init() {
    // Autocomplete works on ALL pages (header search bar)
    initAutocomplete();

    // Collection/search features only if API configured
    if (!CONFIG.apiUrl && !CONFIG.meiliUrl) return;

    // Only run collection logic if we have a product grid
    if (!document.querySelector('.ws-product-grid')) return;

    // bfcache restore (wsScrollBfcache): back from a product can bring the page back
    // without re-running init. With scrollRestoration = 'manual' WebKit (iPhone) may
    // leave it at the top, and the saved key would stay behind to fire on a later load.
    window.addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      try {
        const y = sessionStorage.getItem(scrollKey());
        if (y === null) return;
        sessionStorage.removeItem(scrollKey());
        window.scrollTo(0, parseInt(y, 10) || 0);
      } catch (_) {}
    });

    // Take over scroll restoration: the browser's default fires before our async
    // grid render, so we restore manually once the page is rendered.
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    bindScrollMemory();

    urlToState();
    // Set search query from Liquid (search page)
    if (CONFIG.searchQuery && !state.q) {
      state.q = CONFIG.searchQuery;
    }
    bindCarousel();
    bindMobileDrawer();
    // Single global listener for closing dropdowns (prevents listener leak)
    document.addEventListener('click', () => {
      document.querySelectorAll('.ws-filter-drop').forEach(d => d.classList.remove('ws-drop--open'));
      _openDropdownFacet = null;
    });
    bindGridToggle();

    // Empty-state "clear filters" CTA (static markup, bind once)
    document.querySelector('.ws-no-results-clear')?.addEventListener('click', () => {
      clearFilters();
      executeSearch();
    });

    // Initial search
    // First render normalises the URL (e.g. adds ?sort=newest): replace the entry
    // instead of pushing a second one, or Back from the collection lands on itself.
    state._isPopstate = true;
    executeSearch();

    // Handle back/forward — use replaceState (not pushState) to avoid corrupting history
    window.addEventListener('popstate', () => {
      urlToState();
      state._isPopstate = true;
      executeSearch();
    });
  }

  // Auto-init when DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for external use
  window.WananSearch = { state, executeSearch, clearFilters, setFilter, api };
})();
